/**
 * Music Service - Handles background music using Howler.js
 * Separate from soundService for independent volume/mute control
 */
import { Howl } from 'howler';

// Music track definitions
const MUSIC_TRACKS = {
  // Main game music - loops continuously (Partie 1, 4, 7...)
  main: {
    src: '/sounds/music/main_theme.mp3',
    volume: 0.3,
    loop: true,
  },
  // Vacation theme - alternate main music (Partie 2, 5, 8...)
  vacation: {
    src: '/sounds/music/vacation_theme.mp3',
    volume: 0.3,
    loop: true,
  },
  // Amsterdam theme - third rotation theme (Partie 3, 6, 9...)
  amsterdam: {
    src: '/sounds/music/amsterdam_theme.mp3',
    volume: 0.3,
    loop: true,
  },
  // Round end splash music - plays during scoring and continues into shop
  roundEnd: {
    src: '/sounds/music/round_end.mp3',
    volume: 0.35,
    loop: true,
  },
  // Minigame music - plays during minigames
  minigame: {
    src: '/sounds/music/minigame.mp3',
    volume: 0.35,
    loop: true,
  },
  // Stammtisch lobby music - plays in private room lobby
  stammtisch: {
    src: '/sounds/music/unused.mp3',
    volume: 0.3,
    loop: true,
  },
};

class MusicService {
  constructor() {
    this.tracks = {};
    this.currentTrack = null;
    this.currentTrackName = null;
    this.previousTrackName = null; // For temporary track switching (e.g., minigame)
    this.muted = this.loadMutedState();
    this.masterVolume = this.loadVolumeState();
    this.isPlaying = false;
    this.initialized = false;
  }

  /**
   * Initialize music service - call after user interaction
   */
  init() {
    if (this.initialized) return;

    // Preload all tracks
    Object.entries(MUSIC_TRACKS).forEach(([name, config]) => {
      this.tracks[name] = new Howl({
        src: Array.isArray(config.src) ? config.src : [config.src],
        volume: config.volume * this.masterVolume * (this.muted ? 0 : 1),
        loop: config.loop,
        preload: false, // Don't preload large files
        html5: true, // Required for large files to stream
        onload: () => {
          console.log(`[MusicService] Track "${name}" loaded`);
        },
        onloaderror: (id, error) => {
          console.warn(`[MusicService] Failed to load track "${name}":`, error);
        },
        onplayerror: (id, error) => {
          console.warn(`[MusicService] Failed to play track "${name}":`, error);
        },
      });
    });

    this.initialized = true;
    console.log('[MusicService] Initialized');
  }

  /**
   * Play a music track (stops current if different)
   */
  play(trackName = 'main') {
    if (!this.initialized) {
      this.init();
    }

    const track = this.tracks[trackName];
    if (!track) {
      console.warn(`[MusicService] Unknown track: ${trackName}`);
      return;
    }

    // Already playing this track
    if (this.currentTrackName === trackName && this.isPlaying) {
      return;
    }

    // Stop current track if different
    if (this.currentTrack && this.currentTrackName !== trackName) {
      this.currentTrack.stop();
    }

    // Play new track
    this.currentTrack = track;
    this.currentTrackName = trackName;

    // Set volume based on mute state
    const config = MUSIC_TRACKS[trackName];
    const targetVolume = this.muted ? 0 : config.volume * this.masterVolume;
    track.volume(targetVolume);

    track.play();
    this.isPlaying = true;

    console.log(`[MusicService] Playing: ${trackName}`);
  }

  /**
   * Stop current music
   */
  stop() {
    if (this.currentTrack) {
      this.currentTrack.stop();
      this.isPlaying = false;
      console.log('[MusicService] Stopped');
    }
  }

  /**
   * Pause current music
   */
  pause() {
    if (this.currentTrack && this.isPlaying) {
      this.currentTrack.pause();
      this.isPlaying = false;
    }
  }

  /**
   * Resume paused music
   */
  resume() {
    if (this.currentTrack && !this.isPlaying) {
      this.currentTrack.play();
      this.isPlaying = true;
    }
  }

  /**
   * Toggle mute state
   */
  toggleMute() {
    this.muted = !this.muted;
    this.saveMutedState();
    this.applyMuteState();
    return this.muted;
  }

  /**
   * Set mute state
   */
  setMuted(muted) {
    this.muted = muted;
    this.saveMutedState();
    this.applyMuteState();
  }

  /**
   * Apply mute state to current track
   */
  applyMuteState() {
    if (this.currentTrack && this.currentTrackName) {
      const config = MUSIC_TRACKS[this.currentTrackName];
      const targetVolume = this.muted ? 0 : config.volume * this.masterVolume;

      // Smooth fade
      this.currentTrack.fade(this.currentTrack.volume(), targetVolume, 300);
    }
  }

  /**
   * Check if music is muted
   */
  isMuted() {
    return this.muted;
  }

  /**
   * Set master volume (0-1)
   */
  setMasterVolume(volume) {
    this.masterVolume = Math.max(0, Math.min(1, volume));
    this.saveVolumeState();

    if (this.currentTrack && this.currentTrackName && !this.muted) {
      const config = MUSIC_TRACKS[this.currentTrackName];
      this.currentTrack.volume(config.volume * this.masterVolume);
    }
  }

  /**
   * Get master volume
   */
  getMasterVolume() {
    return this.masterVolume;
  }

  /**
   * Fade out current music
   */
  fadeOut(duration = 1000) {
    if (this.currentTrack && this.isPlaying) {
      this.currentTrack.fade(this.currentTrack.volume(), 0, duration);
      this.currentTrack.once('fade', () => {
        this.currentTrack.stop();
        this.isPlaying = false;
      });
    }
  }

  /**
   * Fade in current music
   */
  fadeIn(trackName = 'main', duration = 1000) {
    if (!this.initialized) {
      this.init();
    }

    const track = this.tracks[trackName];
    if (!track) {
      console.warn(`[MusicService] fadeIn: track "${trackName}" not found`);
      return;
    }

    // Stop current if different
    if (this.currentTrack && this.currentTrackName !== trackName) {
      this.currentTrack.stop();
    }

    this.currentTrack = track;
    this.currentTrackName = trackName;

    const config = MUSIC_TRACKS[trackName];
    const targetVolume = this.muted ? 0 : config.volume * this.masterVolume;

    console.log(`[MusicService] fadeIn: ${trackName}, muted=${this.muted}, masterVolume=${this.masterVolume}, targetVolume=${targetVolume}, trackState=${track.state()}`);

    // Reset track position and ensure it's loaded
    track.stop(); // Fully stop to reset position
    track.volume(targetVolume); // Set volume BEFORE playing
    const playId = track.play();
    console.log(`[MusicService] fadeIn: play() returned id=${playId}, playing=${track.playing()}`);
    this.isPlaying = true;
  }

  /**
   * Play a track temporarily, remembering the current track for later resume.
   * Used for minigames to switch music and return to previous afterward.
   */
  playTemporary(trackName, fadeDuration = 500) {
    console.log(`[MusicService] playTemporary called: ${trackName}, currentTrack: ${this.currentTrackName}, isPlaying: ${this.isPlaying}`);

    // Remember current track (only if not already in temporary mode)
    // If no music is playing, default to 'main' so we have something to resume
    if (!this.previousTrackName) {
      this.previousTrackName = this.currentTrackName || 'main';
    }

    console.log(`[MusicService] Playing temporary: ${trackName} (previous: ${this.previousTrackName})`);

    // Stop current track if playing
    if (this.currentTrack) {
      this.currentTrack.stop();
      this.isPlaying = false;
    }

    // Play the temporary track
    this.fadeIn(trackName, fadeDuration);
  }

  /**
   * Resume the previous track that was playing before playTemporary() was called.
   */
  resumePrevious(fadeDuration = 500) {
    if (!this.previousTrackName) {
      console.log('[MusicService] No previous track to resume');
      return;
    }

    const trackToResume = this.previousTrackName;
    this.previousTrackName = null; // Clear so we don't double-resume

    console.log(`[MusicService] Resuming previous: ${trackToResume}, currentTrack:`, this.currentTrackName, 'isPlaying:', this.isPlaying);

    // Stop current track first
    if (this.currentTrack) {
      this.currentTrack.stop();
      this.isPlaying = false;
    }

    // Then fade in the previous track
    this.fadeIn(trackToResume, fadeDuration);
  }

  // === LocalStorage Persistence ===

  loadMutedState() {
    try {
      const saved = localStorage.getItem('musicMuted');
      return saved === 'true';
    } catch {
      return false;
    }
  }

  saveMutedState() {
    try {
      localStorage.setItem('musicMuted', this.muted.toString());
    } catch {
      // Ignore storage errors
    }
  }

  loadVolumeState() {
    try {
      const saved = localStorage.getItem('musicVolume');
      return saved !== null ? parseFloat(saved) : 0.5;
    } catch {
      return 0.5;
    }
  }

  saveVolumeState() {
    try {
      localStorage.setItem('musicVolume', this.masterVolume.toString());
    } catch {
      // Ignore storage errors
    }
  }
}

// Export singleton instance
const musicService = new MusicService();
export default musicService;
